import Container from '../Container';
import { H1, H3 } from '../Text';
import homeCover from 'assets/images/home-cover.jpg';
import homeCoverSmall from '/assets/images/img-cover2.webp';
import { getImageProps } from 'next/image';
import { useTranslation } from 'next-i18next/pages';
import { IS_ALLMYLES } from 'utils/brand';
import { heroIllustration } from 'assets/illustrations';
import { heroOf } from 'utils/searchTabs';

const coverImageProps = { alt: 'home-cover', sizes: '100vw', priority: true };
const {
  props: { srcSet: desktopCoverSrcSet },
} = getImageProps({ ...coverImageProps, src: homeCover });
const { props: mobileCoverProps } = getImageProps({
  ...coverImageProps,
  src: homeCoverSmall,
});
const { props: heroProps } = getImageProps({
  src: heroIllustration,
  alt: '',
  unoptimized: true,
});
// Phones get this instead of the illustration, which is hidden there: an
// eager <img> is downloaded even when hidden, a <source> that does not match
// is not.
const BLANK_GIF =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface IHomeCoverProps {
  // The chosen search tab: the hero says what it offers (WHIT-506).
  tab?: string;
}

const HomeCover: React.FC<IHomeCoverProps> = ({ tab }) => {
  const { t } = useTranslation();
  const hero = heroOf(tab);
  return (
    <div
      id="search-flights"
      className="relative h-[28.5rem] sm:h-[27.063rem] md:h-[26rem] 2xl:h-[27.063rem] 3xl:h-[27.5rem]">
      {/* The LCP element on every viewport. One <picture> rather than two
          <Image>s, so each viewport fetches only its own file, eagerly and at
          high priority: a hidden eager <img> is still downloaded, and a
          `priority` preload has no media query. */}
      {IS_ALLMYLES ? (
        // Allmyles: a flat hero with the 3D illustration on the right, from
        // sm up; on phones the title sits on the flat colour (WHIT-493).
        <div className="h-full w-full bg-tt-white-purple overflow-hidden">
          <div className="relative h-full max-w-[75rem] mx-auto">
            <picture>
              <source media="(min-width: 640px)" srcSet={heroProps.src} />
              <img
                src={BLANK_GIF}
                width={heroProps.width}
                height={heroProps.height}
                alt=""
                fetchPriority="high"
                className="hidden sm:block absolute right-0 top-10 h-[27.5rem] w-auto"
              />
            </picture>
          </div>
        </div>
      ) : (
        <picture className="block h-full w-full">
          <source media="(min-width: 640px)" srcSet={desktopCoverSrcSet} />
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            {...mobileCoverProps}
            style={{ ...mobileCoverProps.style, color: undefined }}
            className="object-cover h-full w-full"
          />
        </picture>
      )}
      <Container
        isHome
        containerClassName="absolute 3xl:top-[9.5rem] top-[6.35rem] smallmedium:top-[7.35rem] sm:top-[8.25rem] xl:top-[8.35rem] mt-4"
        className="grid content-center">
        <H1 gopher>{t(hero.title)}</H1>
        <H3
          as="p"
          className="!text-[1.5rem] leading-normal mt-4 !sm:text-[1.5rem] md:text-[1.5rem] pr-[1.625rem] sm:pr-0 lg:max-w-[34rem]">
          {t(hero.subtitle)}
        </H3>
      </Container>
    </div>
  );
};

export default HomeCover;
